/**
 * THE FISHING HUD. What the sheet says about the fish, as opposed to about the sea.
 *
 * -- WHAT WAS MISSING, MEASURED RATHER THAN ASSUMED -----------------------------
 *
 * Round 2 of this lane read its brief as "the game has an interface, deepen it", and
 * put three careful things INSIDE the ledger. The owner looked at the running game a
 * second time and said "there still is a lack of GUI", and the measurement behind
 * that verdict is flat: the only place the game named its controls was one
 * `<span class="hint">` inside a `#strip` that is `display: none` without `body.dev`,
 * so a player was never told the letter `L` existed and could not reach anything
 * built behind it. Level, experience, coins and the personal best were computed,
 * persisted, and rendered nowhere.
 *
 * Round 3 is therefore the persistent half, in the same visual language:
 *
 *   THE SHEET HAS A FOOT, and it is always on screen. Level, what is owed to the
 *   next, the tin, and - until the ledger has been opened once - the sentence that
 *   says the ledger is there at all. `account.ts` carries that argument.
 *   THE QUARRY POLE CARRIES THE RECORD. `SimState.records` is real now and nothing
 *   displayed it; the heaviest one of the fish you are after stands under its count.
 *   THE CATCH SPEAKS ITS WEIGHT, and says so when it is the heaviest yet, which is
 *   the moment a record is worth anything.
 *   THE RULE UNDER A FISH BECOMES THE HOURS IN IT (round 2). A mark the sheet
 *   already draws, given a job; `hours.ts` carries the argument. Round 3 finally
 *   LABELS it, at the foot, while the book is open.
 *   THE PHRASE SAYS WHAT YOU ARE AFTER, now in every state rather than only when
 *   the boat is on its own course.
 *   THE OPEN BOOK TALLIES ITSELF on its second line: species landed, weight landed,
 *   and the heaviest single fish, which the engine records at last.
 *
 * -- WHAT IT DOES NOT DO -------------------------------------------------------
 *
 * It adds no colour, no face, no tone and no ornament. The sheet's palette stays at
 * five values and its accent count stays at ZERO, against a measured median of three
 * across 244 leader sites; its two written roles stay two; the torn edge stays the
 * only ornament in the interface. Every glyph and every coordinate is authored on
 * the 960x540 grid CONTRACT 1.5e rules and doubled by the same integer 2 as the sea,
 * because all of it is painted into the chart's own index bitmap by the chart's own
 * primitives. There is nothing here for a browser to anti-alias.
 *
 * IT DOES ADD A PERMANENTLY VISIBLE COUNTER, and that is a correction rather than a
 * relaxation. Round 2 refused one on the ground that CONTRACT section 6's "PRESENCE
 * CHANGES NOTHING" forbade it. Read whole, that clause says the game "plays
 * identically whether it is watched or not" and is instrumented by two controllers
 * over one seed producing identical states. It is a claim about the simulation. A
 * figure a player can see does not make presence pay; it makes absence legible.
 * Nothing here is a live tick either: `countWords` rounds every standing figure so
 * the foot moves once per hundred coins rather than once per catch, which is what
 * keeps the sheet at rest genuinely at rest.
 */

export {
  hoursInk,
  hoursKey,
  masteryFraction,
  paintHours,
  ruleSpans,
  type HoursRows,
  type RuleSpan,
} from './hours';

export {
  afterClause,
  bookLine,
  countWords,
  personalBest,
  speciesLanded,
  weightWords,
} from './book';

export {
  MASTERY_KEY,
  accountLine,
  catchLine,
  recordNote,
  type LedgerHint,
} from './account';

export { chartSpeech, type Speech, type SpokenRow } from './speech';

export { makeReading, type ReadingNodes, type ReadingSurface } from './reading';

import type { ChartCopy, Leaf } from '../render/chart';
import { platesReady } from '../render/chart-plates';
import { placeFootLine, quarryOf } from '../render/chart-copy';
import type { LedgerPage } from '../render/chart-control';
import type { Bitmap } from '../render/glyphs';
import type { SimState } from '../sim/index';
import { MASTERY_KEY, accountLine, recordNote, type LedgerHint } from './account';
import { afterClause } from './book';
import type { HoursRows } from './hours';

/**
 * EVERYTHING ON THE SHEET THAT COMES OUT OF THE SAVE, ASSEMBLED IN ONE PLACE.
 *
 * The host owns the sea's half of the copy - where the boat is, what the player just did, how
 * far the paper has lifted - because all of it is transient and none of it is state. This owns
 * the other half, and it is one function for a reason that is a fence rather than a tidiness:
 * `earned-is-rendered.test.ts` proves that every earned per-species map in `FIELD_CLASS` moves
 * a pixel of the interface, and a proof like that is only worth anything if the pixels it
 * measures are the ones the game draws. Two assemblies - one in the host, one in the test -
 * would let the fence stay green while the running build stopped reading a field.
 */
export interface HudInputs {
  readonly state: SimState;
  /** Where the boat is and what it is doing, before the target clause is added to it. */
  readonly phrase: string;
  readonly aside: string;
  readonly asideInk: number;
  readonly lift: number;
  readonly leaf: Leaf;
  readonly cursor: number;
  readonly exit: boolean;
  readonly ledger: readonly LedgerPage['rows'][number][];
  readonly hint: LedgerHint;
  /** The id behind each row, index-aligned with `ledger`. `LedgerPage.ids`. */
  readonly ids: readonly string[];
  /** Every baked plate by species id. Empty until the host has loaded them. */
  readonly plates: ReadonlyMap<string, Bitmap>;
}

/**
 * WHICH ANIMAL THE SHEET IS DRAWING, AND WHY IT IS THE ONE UNDER THE HAND.
 *
 * The plate is a plate OF A SPECIES, so it follows the hand down the species leaf and there is
 * nothing else it could sensibly follow: the quarry is what the boat is fishing FOR, which is
 * often not what the player is reading about, and pinning the box to the quarry would leave the
 * same fish on the page while the hand walked past twenty-three others.
 *
 * IT DRAWS SPECIES THE PLAYER HAS NOT LANDED, and that is a decision rather than an oversight.
 * The alternative - a plate only for a species with a catch against it - makes the ledger's
 * geometry JUMP as the hand crosses from a landed row to an unlanded one, because `plated()`
 * narrows the columns on the plate's presence. A field guide prints the plate of a fish you
 * have not caught; that is most of what a field guide is for. Nothing is disclosed by it that
 * the sheet was not already disclosing, because all twenty-four names are on the page from the
 * first frame and have been since the round that tightened the columns to fit them.
 *
 * The three conditions are the sheet's own: the paper has to be lifted, the leaf has to be the
 * species leaf, and the hand has to be on a row. `chart.ts` re-checks the leaf in `plated()` for
 * its own reasons - a places row would be clipped by a narrowed column - and this does not rely
 * on that, because a fence that depends on a fact holding somewhere else is not a fence.
 */
export function plateUnderHand(input: {
  readonly leaf: Leaf;
  readonly lift: number;
  readonly cursor: number;
  readonly ids: readonly string[];
  readonly plates: ReadonlyMap<string, Bitmap>;
}): Bitmap | null {
  if (input.leaf !== 'species' || input.lift <= 0 || input.cursor < 0) return null;
  // ALL TWENTY-FOUR OR NONE. The argument is on `platesReady`; the call is here so that a
  // partly-loaded set draws paper rather than four painted fish beside twenty stubs.
  if (!platesReady(input.plates)) return null;
  const id = input.ids[input.cursor];
  if (id === undefined || id === '') return null;
  return input.plates.get(id) ?? null;
}

export function hudCopy(input: HudInputs): ChartCopy {
  const state = input.state;
  const quarry = quarryOf(state);
  return {
    phrase: afterClause(input.phrase, state),
    aside: input.aside,
    asideInk: input.asideInk,
    markSpecies: quarry,
    markCount: quarry === null ? 0 : (state.catches[quarry] ?? 0),
    record: recordNote(state, quarry),
    // THE FOOT: the standing account, or the open page's own business. One line, one job at a
    // time.
    //
    // Shut, it is the account. Open on the species leaf it is that page's key, because the
    // mastery measure under a row is otherwise a mark with no explanation anywhere on the
    // sheet. Open on the PLACES leaf it used to be the EMPTY STRING - a whole line of the
    // sheet doing nothing, on the one page with a written world behind it - and it now carries
    // the landfall under the hand: its area, its distance WITH THE UNIT SAID, and what the
    // island is for. `placeFootLine` carries that argument.
    foot:
      input.lift > 0
        ? input.leaf === 'species'
          ? MASTERY_KEY
          : placeFootLine(input.ledger[input.cursor < 0 ? 0 : input.cursor])
        : accountLine(state, input.hint),
    lift: Math.max(0, Math.min(1, input.lift)),
    ledger: input.ledger,
    leaf: input.leaf,
    cursor: input.cursor,
    exit: input.exit,
    plate: plateUnderHand(input),
  };
}

/**
 * The mastery behind each row of a page, or null where a row has no such quantity.
 *
 * ONLY THE SPECIES LEAF HAS ONE, and that is a fact about the data rather than a
 * decision about the drawing: a landfall has no mastery, and a species still locked
 * cannot have been landed, so no hour can have been spent on it. Both come back
 * null and their rules are left exactly as the chart painted them. Reading the ids
 * off the page rather than off `SPECIES` is what keeps this aligned when the page's
 * row order changes, which is the same reason `chart-control` publishes them.
 */
export function hoursFor(page: LedgerPage, state: SimState): HoursRows {
  if (page.leaf !== 'species') return page.rows.map(() => null);
  return page.rows.map((row, i) => {
    if (!row.reachable) return null;
    const id = page.ids[i];
    if (id === undefined || id === '') return null;
    return state.mastery[id] ?? 0;
  });
}
